/**
 * Design Check client invite management — shared by the admin Access
 * Management tab and the /design-check/invites page for sales staff.
 * Create expiring magic-link invites, copy the link + one-time password,
 * extend/revoke access, and follow each client's activity: uploads, DFM
 * results, Bob conversations and a full engagement timeline.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card } from '../ui';

const day = 24 * 60 * 60 * 1000;

const ACTIVITY_LABEL = {
  signin: 'Signed in',
  upload: 'Uploaded',
  chat: 'Asked Bob',
  guide: 'Read guide'
};

function InvitePanel({ showMessage }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ label: '', clientEmail: '', expiresInDays: 14, maxUploads: 20 });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // { invite, tempPassword, inviteUrl }
  const [expanded, setExpanded] = useState(null);
  const [conversation, setConversation] = useState(null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`
  }), []);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/design-check/admin/invites', { headers: headers() });
      const data = await res.json();
      if (data.success) setInvites(data.invites);
    } catch (err) {
      showMessage && showMessage('Failed to load Design Check invites', 'error');
    } finally {
      setLoading(false);
    }
  }, [headers, showMessage]);

  useEffect(() => { refresh(); }, [refresh]);

  const absoluteUrl = (path) => `${window.location.origin}${path}`;

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      showMessage && showMessage(`${what} copied to clipboard`, 'success');
    } catch (err) {
      window.prompt(`Copy this ${what}:`, text);
    }
  };

  const createInvite = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return;
    setCreating(true);
    try {
      const res = await fetch('/api/design-check/admin/invites', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed');
      setCreated(data);
      setForm({ label: '', clientEmail: '', expiresInDays: 14, maxUploads: 20 });
      refresh();
    } catch (err) {
      showMessage && showMessage(err.message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const extend = async (invite, days) => {
    await fetch(`/api/design-check/admin/invites/${invite.id}`, {
      method: 'PATCH',
      headers: headers(),
      body: JSON.stringify({ extendDays: days })
    });
    showMessage && showMessage(`Extended "${invite.label}" by ${days} days`, 'success');
    refresh();
  };

  const resetLogin = async (invite) => {
    try {
      const res = await fetch(`/api/design-check/admin/invites/${invite.id}/reset-password`, {
        method: 'POST',
        headers: headers()
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed');
      setCreated(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      showMessage && showMessage(`New login generated for "${invite.label}"`, 'success');
    } catch (err) {
      showMessage && showMessage(err.message, 'error');
    }
  };

  const revoke = async (invite) => {
    if (!window.confirm(`Revoke access for "${invite.label}"? Their link and login stop working immediately.`)) return;
    await fetch(`/api/design-check/admin/invites/${invite.id}`, { method: 'DELETE', headers: headers() });
    showMessage && showMessage(`Revoked "${invite.label}"`, 'success');
    refresh();
  };

  const viewConversation = async (invite) => {
    setConversation({ invite, messages: null });
    try {
      const res = await fetch(`/api/design-check/admin/conversations/${invite.username}`, { headers: headers() });
      const data = await res.json();
      setConversation({ invite, messages: data.success ? data.messages : [] });
    } catch (err) {
      setConversation({ invite, messages: [] });
    }
  };

  const statusColor = {
    active: 'var(--rf-success)', pending: 'var(--rf-info)',
    expired: 'var(--rf-warning)', revoked: 'var(--rf-danger)'
  };

  const cellStyle = { padding: '10px 12px', borderBottom: '1px solid var(--rf-gray-50)', fontSize: 13 };

  return (
    <Card className="rf-tab-content">
      <h3 style={{ marginTop: 0 }}>Design Check — client invites</h3>
      <p style={{ color: 'var(--rf-text-secondary)', fontSize: 13, marginTop: 4 }}>
        Send a client an invite link and they can upload STEP files, get DFM reports and
        talk to Bob — nothing else in the portal. Access dies automatically on the expiry date.
      </p>

      <form onSubmit={createInvite} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', margin: '18px 0 24px' }}>
        <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          Client / project name *
          <input
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Acme Aerospace — bracket"
            required
            style={{ padding: '9px 11px', minWidth: 220 }}
          />
        </label>
        <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          Client email (optional)
          <input
            value={form.clientEmail}
            onChange={(e) => setForm({ ...form, clientEmail: e.target.value })}
            placeholder="jane@acme.com"
            type="email"
            style={{ padding: '9px 11px', minWidth: 200 }}
          />
        </label>
        <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          Expires in
          <select
            value={form.expiresInDays}
            onChange={(e) => setForm({ ...form, expiresInDays: parseInt(e.target.value, 10) })}
            style={{ padding: '9px 11px' }}
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        </label>
        <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          Max uploads
          <input
            type="number"
            min={1}
            max={200}
            value={form.maxUploads}
            onChange={(e) => setForm({ ...form, maxUploads: parseInt(e.target.value, 10) || 20 })}
            style={{ padding: '9px 11px', width: 90 }}
          />
        </label>
        <Button type="submit" disabled={creating}>
          {creating ? 'Creating…' : '+ Create invite'}
        </Button>
      </form>

      {created && (
        <div style={{
          background: 'var(--rf-success-light)', border: '1px solid rgba(var(--rf-success-rgb), 0.4)',
          borderRadius: 12, padding: 16, marginBottom: 22
        }}>
          <b style={{ fontSize: 14 }}>Invite ready for {created.invite.label}</b>
          <p style={{ fontSize: 12.5, color: 'var(--rf-text-secondary)', margin: '6px 0 12px' }}>
            Send the client the link below — it signs them straight in until{' '}
            {new Date(created.invite.expiresAt).toLocaleDateString()}. The username and password are a
            fallback for logging in at {absoluteUrl('/login')} — <b>the password is only shown this once</b>.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code style={{ fontSize: 12, wordBreak: 'break-all', flex: 1 }}>{absoluteUrl(created.inviteUrl)}</code>
              <Button size="sm" onClick={() => copy(absoluteUrl(created.inviteUrl), 'Invite link')}>Copy link</Button>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code style={{ fontSize: 12 }}>{created.invite.username} / {created.tempPassword}</code>
              <Button size="sm" onClick={() => copy(`${created.invite.username} / ${created.tempPassword}`, 'Credentials')}>Copy login</Button>
              <Button size="sm" variant="secondary" onClick={() => setCreated(null)}>Done</Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : invites.length === 0 ? (
        <p style={{ color: 'var(--rf-text-tertiary)' }}>No invites yet — create the first one above.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--rf-text-tertiary)' }}>
                <th style={cellStyle}>Client</th>
                <th style={cellStyle}>Status</th>
                <th style={cellStyle}>Expires</th>
                <th style={cellStyle}>Uploads</th>
                <th style={cellStyle}>Last active</th>
                <th style={cellStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <React.Fragment key={invite.id}>
                  <tr>
                    <td style={cellStyle}>
                      <b>{invite.label}</b>
                      <div style={{ fontSize: 11.5, color: 'var(--rf-text-tertiary)' }}>
                        {invite.clientEmail || invite.username}
                      </div>
                      {invite.createdBy === 'shopify-bob' && (
                        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, color: 'var(--rf-warning)', textTransform: 'uppercase' }}>
                          Self-serve — Shopify Bob
                        </div>
                      )}
                    </td>
                    <td style={cellStyle}>
                      <span style={{ color: statusColor[invite.status], fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>
                        {invite.status}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      {new Date(invite.expiresAt).toLocaleDateString()}
                      <div style={{ fontSize: 11.5, color: 'var(--rf-text-tertiary)' }}>
                        {invite.expiresAt > Date.now() ? `${Math.ceil((invite.expiresAt - Date.now()) / day)}d left` : '—'}
                      </div>
                    </td>
                    <td style={cellStyle}>{invite.uploadCount || 0} / {invite.maxUploads}</td>
                    <td style={cellStyle}>
                      {invite.lastActiveAt ? new Date(invite.lastActiveAt).toLocaleString() : 'never'}
                    </td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                      <Button size="sm" variant="secondary" onClick={() => copy(absoluteUrl(invite.inviteUrl), 'Invite link')}>Link</Button>{' '}
                      <Button size="sm" variant="secondary" onClick={() => resetLogin(invite)}>Reset login</Button>{' '}
                      <Button size="sm" variant="secondary" onClick={() => extend(invite, 14)}>+14d</Button>{' '}
                      {invite.status !== 'revoked' && (
                        <Button size="sm" variant="danger" onClick={() => revoke(invite)}>Revoke</Button>
                      )}{' '}
                      <Button size="sm" variant="secondary" onClick={() => setExpanded(expanded === invite.id ? null : invite.id)}>
                        {expanded === invite.id ? 'Hide' : `Activity (${invite.jobs.length})`}
                      </Button>
                    </td>
                  </tr>
                  {expanded === invite.id && (
                    <tr>
                      <td colSpan={6} style={{ ...cellStyle, background: 'var(--rf-gray-50)' }}>
                        {invite.jobs.length === 0 ? (
                          <span style={{ color: 'var(--rf-text-tertiary)' }}>No uploads yet.</span>
                        ) : (
                          invite.jobs.map((job) => (
                            <div key={job.id} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '5px 0', fontSize: 12.5 }}>
                              <b style={{ minWidth: 200 }}>{job.fileName}</b>
                              <span>{job.status}</span>
                              {job.summary && <span>overall: <b>{job.summary.overall}</b></span>}
                              {job.recommended && (
                                <span>
                                  {job.recommended.orientationId} · {job.recommended.nozzleMm}mm nozzle · {job.recommended.layerHeightMm}mm layers
                                </span>
                              )}
                              <a
                                href={`/design-check/analysis/${job.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'var(--rf-primary-light)' }}
                              >
                                Open report →
                              </a>
                            </div>
                          ))
                        )}
                        <div style={{ marginTop: 8 }}>
                          <Button size="sm" variant="secondary" onClick={() => viewConversation(invite)}>
                            View Bob conversation
                          </Button>
                        </div>

                        <div style={{ marginTop: 14 }}>
                          <b style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--rf-text-tertiary)' }}>
                            Timeline
                          </b>
                          {(invite.activity || []).length === 0 ? (
                            <div style={{ color: 'var(--rf-text-tertiary)', fontSize: 12.5, marginTop: 4 }}>
                              No activity recorded yet.
                            </div>
                          ) : (
                            [...invite.activity].reverse().slice(0, 50).map((event, i) => (
                              <div key={i} style={{ display: 'flex', gap: 12, padding: '4px 0', fontSize: 12.5 }}>
                                <span style={{ color: 'var(--rf-text-tertiary)', minWidth: 140 }}>
                                  {new Date(event.at).toLocaleString()}
                                </span>
                                <b style={{ minWidth: 80 }}>{ACTIVITY_LABEL[event.type] || event.type}</b>
                                {event.detail && (
                                  <span style={{ color: 'var(--rf-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {event.detail}
                                  </span>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {conversation && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
        }} onClick={() => setConversation(null)}>
          <div
            style={{
              background: 'var(--rf-bg-elevated)', borderRadius: 14, padding: 22,
              maxWidth: 640, width: '100%', maxHeight: '80vh', overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ marginTop: 0 }}>Bob ↔ {conversation.invite.label}</h4>
            {conversation.messages === null ? (
              <p>Loading…</p>
            ) : conversation.messages.length === 0 ? (
              <p style={{ color: 'var(--rf-text-tertiary)' }}>No conversation yet.</p>
            ) : (
              conversation.messages.map((m, i) => (
                <div key={m.id || i} style={{ marginBottom: 12 }}>
                  <b style={{ fontSize: 11.5, textTransform: 'uppercase', color: m.role === 'user' ? 'var(--rf-info)' : 'var(--rf-primary-light)' }}>
                    {m.role === 'user' ? conversation.invite.label : 'Bob'}
                  </b>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{m.content}</div>
                </div>
              ))
            )}
            <Button variant="secondary" onClick={() => setConversation(null)}>Close</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default InvitePanel;
